import { useMemo } from "react";
import { validateDistro, type ValidationResult } from "@aidkitorg/types/lib/distroValidator/distroValidator";
import { Alert, Badge, Button, Card, CardHeader, CardBody, Heading, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Modal } from "@aidkitorg/component-library";


export function DistroValidation(props: { survey: any }) {
  const validatorResults = useMemo(() => validateDistro(props.survey), [props.survey]);
  return <DistroValidationDisplay results={validatorResults} />;
}

export function DistroValidationModal(props:{
  results: ValidationResult[],
  onClose: ({openValidationPanel, publishAnyway}: {openValidationPanel: boolean, publishAnyway: boolean}) => void,
}) {
  // const hasErrors = props.results.some(result => result.severity === 'error');

  return (
    <Modal
      open={true}
      onClose={() => props.onClose({openValidationPanel: false, publishAnyway: false})}
      title="Distro Validation Errors"
      size="lg"
      preventBackdropClick={false}
    >
      <div className="max-h-96 overflow-y-auto mb-4">
        <DistroValidationDisplay results={props.results} />
      </div>
      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={() => props.onClose({openValidationPanel: true, publishAnyway: false})}
        >
          Open Validation Panel
        </Button>
        {/* {!hasErrors && ( */}
        <Button
          variant="danger"
          onClick={() => props.onClose({openValidationPanel: false, publishAnyway: true})}
        >
          Publish anyways
        </Button>
        {/* )} */}
      </div>
    </Modal>
  );
}
function DistroValidationDisplay({results}: {
  results: ValidationResult[],
}) {
  const dedupedResults = useMemo(() => {
    const grouped = new Map<string, ValidationResult[]>();

    results.forEach((result) => {
      const key = `${result.name}|${result.result.message || ''}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(result);
    });

    return Array.from(grouped.entries()).map(([key, groupResults]) => ({
      key,
      name: groupResults[0].name,
      description: groupResults[0].description,
      severity: groupResults[0].severity,
      message: groupResults[0].result.message,
      components: groupResults.map(r => ({
        component: r.component,
        breadcrumbs: r.breadcrumbs
      }))
    }));
  }, [results]);

  const severityCounts = useMemo(() => {
    const counts = { error: 0, warning: 0, info: 0 };
    dedupedResults.forEach((result) => {
      counts[result.severity]++;
    });
    return counts;
  }, [dedupedResults]);

  const getAlertVariant = (severity: ValidationResult['severity']) => {
    switch (severity) {
      case 'error': return 'danger';
      case 'warning': return 'warning';
      case 'info': return 'primary';
      default: return 'secondary';
    }
  };

  const getSeverityIcon = (severity: ValidationResult['severity']) => {
    switch (severity) {
      case 'error': return 'ExclamationTriangleIcon';
      case 'warning': return 'ExclamationTriangleIcon';
      case 'info': return 'InformationCircleIcon';
      default: return undefined;
    }
  };

  if (results.length === 0) {
    return (
      <Card>
        <CardHeader title={<Heading level="h2">Validation Summary</Heading>} />
        <CardBody>
          <Alert variant="success" mode="inline">
            ✅ No issues found!
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={<Heading level="h2">Validation Summary</Heading>} />
      <CardBody className="space-y-4">
        {/* Summary badges */}
        <div className="flex gap-2 flex-wrap">
          {severityCounts.error > 0 && (
            <Badge variant="error">
              {severityCounts.error} error{severityCounts.error !== 1 ? 's' : ''}
            </Badge>
          )}
          {severityCounts.warning > 0 && (
            <Badge variant="warning">
              {severityCounts.warning} warning{severityCounts.warning !== 1 ? 's' : ''}
            </Badge>
          )}
          {severityCounts.info > 0 && (
            <Badge variant="info">
              {severityCounts.info} info{severityCounts.info !== 1 ? '' : ''}
            </Badge>
          )}
        </div>

        {/* Individual results */}
        <div className="space-y-3">
          {dedupedResults.map((result, index) => (
            <Alert
              key={result.key}
              variant={getAlertVariant(result.severity)}
              mode="block"
              title={`${result.message}`}
              iconName={getSeverityIcon(result.severity)}
            >
              <div className="text-sm">{result.name}: {result.description}</div>
              <Accordion type="multiple" className="mt-3">
                {result.components.map((componentData, componentIndex) => (
                  <AccordionItem key={`${result.key}-component-${componentIndex}`} value={`component-${index}-${componentIndex}`}>
                    <AccordionTrigger className="text-sm">
                      <nav className="text-sm text-gray-600">
                        <ol className="flex flex-wrap items-center gap-2">
                          {componentData.breadcrumbs.map((breadcrumb, idx) => (
                            <li key={idx} className="flex items-center">
                              {idx > 0 && <span className="mx-1 text-gray-400">›</span>}
                              <span className={idx === componentData.breadcrumbs.length - 1 ? "text-gray-900 font-medium" : "text-gray-600"}>
                                {breadcrumb}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </nav>
                    </AccordionTrigger>
                    <AccordionContent>
                      <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-scroll max-w-full">
                        <code>{JSON.stringify(componentData.component, null, 2)}</code>
                      </pre>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Alert>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}
